import { DeliveryLogType, EventLog, ExportStatus } from '@dev/ee-api/ee-api-models';
import {
    ExportLogsAction,
    FetchEventsCountsAction,
    LoadInprogressStatsStartAction,
    LoadInprogressStatsStartDelayedAction,
    LoadLogsStartAction,
    LoadNotificationLogsAction,
    LogTypesString,
    LogsActionTypesEnum,
    NotificationLogsTypes,
    ResendStartAction,
} from '@api/store/logs/types';
import {
    ExportLogsError,
    ExportLogsStart,
    ExportLogsSuccess,
    LoadCancelInprogressError,
    LoadCancelInprogressSuccess,
    LoadInprogressStatsError,
    LoadInprogressStatsErrorDelayed,
    LoadInprogressStatsStart,
    LoadInprogressStatsSuccess,
    LoadInprogressStatsSuccessDelayed,
    LoadLogsError,
    LoadLogsSuccess,
    LoadNotificationLogsSuccess,
    ResendError,
    ResendSuccess,
    SetLoaderInprogressStats,
    UpdateEventsCounts,
} from '@api/store/logs/actions';
import { ExportsPaths, StatisticsPaths } from '@api/services/dictionaries/paths';
import { HideToast, ShowToast } from '@api/store/toast/actions';
import { ApplicationState as State, getRouterStore } from '@api/store';
import { call, delay, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
    checkTaskStatus,
    emailsCancel,
    exportEventslogs,
    exportNotificationsEvents,
    loadEventLogCounts,
    loadEventsGET,
    loadInProgressStats,
    loadLogs,
    loadNotificicationsEventsCountsGET,
    loadNotificicationsEventsGET,
    resendByMessageid,
} from '@api/services/ElasticApi';
import { isEmpty, isEqual, isNil, omit, omitBy, slice } from 'lodash-es';

import { DEFAULT_PER_PAGE_COUNT } from '@api/services/constants';
import { EventLogParams } from '@api/store/reports/types';
import { LoadExports } from '@api/store/exports/actions';
import { push as Push } from 'redux-first-history';
import dayjs from 'dayjs';
import { errorToast } from './commonGenerators/errorToast';
import { headersParser } from '@api/services/tools/headersParser';
import i18n from '@api/i18n';
import { parseUrlParams } from '@api/services/tools/handleUrlParams';
import { stringify } from 'query-string';

const LOAD_IN_PROGRESS_STATS_DELAYED_START = '@@logs/LOAD_IN_PROGRESS_STATS_DELAYED_START';
const getInprogressActivePage = (state: State): number => state.logs.inProgressActivePage;
const getInprogressLogPage = (state: State): number => state.logs.inProgressLogPage;
const getInprogressStatuses = (state: State): DeliveryLogType[] => state.logs.statusesInProgress;
const getInprogressDelayedCount = (state: State): number => state.logs.inprogressStatsDelayed?.Count;
const getInProgressDelayedLoopId = (state: State): string => state.logs.inProgressDelayedLoopId;
const getLogs = (state: State) => state.logs.logList;
const getNotifications = (state: State) => state.logs.notificationList;
function* handleURLQuery(payload, type: string, activePage: number): any {
    const { location } = yield select(getRouterStore);
    let queryParams = parseUrlParams(location.search);

    activePage < 2 ? (queryParams = omit(queryParams, 'page')) : (queryParams.page = activePage.toString());

    if (!isEqual(queryParams, parseUrlParams(location.search))) {
        yield Push({ search: stringify(queryParams, { arrayFormat: 'comma' }) });
    }
}

const getOffsetAndLimit = (activePage: number, perPage?: number): { offset: number; limit: number } => {
    const itemsOnPage = perPage || DEFAULT_PER_PAGE_COUNT;
    const offset = itemsOnPage * activePage - itemsOnPage;
    const limit = itemsOnPage;

    return { offset, limit };
};

//TODO: when moved all filerts to be based only on url move it to component so saga won't have any additional task than to retrive values based on passed props.

function* fetchLogs(action: LoadLogsStartAction): any {
    try {
        const { payload } = action;

        const {
            inProgressLogPage = yield select(getInprogressLogPage),
            statuses = yield select(getInprogressStatuses),
            onlyLastEvent,
        } = payload;
        const currentLogsList: EventLog = yield select(getLogs) || [];
        const { offset, limit } = getOffsetAndLimit(inProgressLogPage, payload.perPage);
        const params = {
            statuses,
            messageCategory: payload?.messageCategory,
            from: payload.from,
            to: payload.to,
            channelName: payload.channelName,
            limit,
            offset,
            includeEmail: payload.includeEmail,
            includeSms: payload.includeSms || false,
            email: payload.email,
            ipaddress: payload.ipaddress,
        };

        //OKR-7745 we are moving to cursor from offset, but we still need it for the proper handling of data, we need to apply this only for loadEventsGET, so please keep it in mind.
        if (offset && !onlyLastEvent) {
            params.to = dayjs.utc(currentLogsList.Recipients?.at?.(-1)?.EventDate).toJSON();
            params.offset = null;
        }

        const filteredParams = omitBy(params, isNil);

        const apiCall = onlyLastEvent ? loadLogs : loadEventsGET;

        const logs = yield call(apiCall, filteredParams);

        const totalLogsCount = parseInt(logs.headers['x-total-count']) || 0;

        const updatedLogs: EventLog = logs.data;
        const hasMoreLogs = !!updatedLogs.Recipients?.length;
        if (offset) {
            updatedLogs.Recipients = [...currentLogsList.Recipients, ...updatedLogs.Recipients];
        }

        yield put(LoadLogsSuccess({ logList: updatedLogs, inProgressLogPage, totalLogsCount, hasMoreLogs }));
    } catch (e) {
        yield put(LoadLogsError());
        yield call(errorToast, e);
    }
}

function* fetchEventsCount(action: FetchEventsCountsAction): any {
    try {
        const { payload } = action;
        const { inProgressLogPage = yield select(getInprogressLogPage), eventType = LogTypesString.EMAIL } = payload;

        const { offset, limit } = getOffsetAndLimit(inProgressLogPage, payload.perPage);

        const params: EventLogParams = {
            statuses: [DeliveryLogType.All],
            messageCategory: payload.messageCategory,
            from: payload.from,
            to: payload.to,
            channelName: payload.channelName,
            limit,
            offset,
            includeEmail: payload.includeEmail,
            includeSMS: payload.includeSms || false,
            email: payload.email,
            ipaddress: payload.ipaddress,
        };
        const filteredParams = omitBy(params, isNil);

        const availableApiCall = {
            [LogTypesString.NOTIFICATION]: loadNotificicationsEventsCountsGET,
            [LogTypesString.EMAIL]: loadEventLogCounts,
            [LogTypesString.SMS]: loadEventLogCounts,
        };

        const logs = yield call(availableApiCall?.[eventType], filteredParams);

        const updatedCounts = headersParser(logs?.headers);
        yield put(UpdateEventsCounts(updatedCounts));
    } catch (e) {
        yield call(errorToast, e);
        yield put(LoadLogsError());
    }
}

function* fetchNotificationLogs(action: LoadNotificationLogsAction): any {
    try {
        const currentLogsList: EventLog = yield select(getNotifications) || [];
        const { payload } = action;
        const {
            inProgressLogPage = yield select(getInprogressLogPage),
            statuses = yield select(getInprogressStatuses),
            messageCategory,
            from,
            to,
            email,
        } = payload;

        const { offset, limit } = getOffsetAndLimit(inProgressLogPage, payload.perPage);

        const params = {
            statuses,
            messageCategory,
            from,
            to,
            limit,
            offset,
            email,
        };
        const logs = yield call(loadNotificicationsEventsGET, params);
        const totalLinkCount = parseInt(logs.headers['x-total-count']) || 0;
        if (totalLinkCount) {
            yield put(UpdateEventsCounts(headersParser(logs?.headers)));
        }

        const updatedLogs: EventLog = logs?.data;
        const hasMoreLogs = !!updatedLogs.Recipients?.length;
        if (offset) {
            updatedLogs.Recipients = [...currentLogsList?.Recipients, ...updatedLogs?.Recipients];
        }

        yield put(LoadNotificationLogsSuccess(updatedLogs, inProgressLogPage, totalLinkCount, hasMoreLogs));
    } catch (e) {
        yield put(LoadLogsError());
        yield call(errorToast, e);
    }
}

function* fetchInprogressStats(action: LoadInprogressStatsStartAction): any {
    try {
        const { payload, type } = action;
        const { inProgressActivePage = yield select(getInprogressActivePage) } = payload;

        yield handleURLQuery(payload, type, inProgressActivePage);

        const inprogressStats = yield call(loadInProgressStats);
        yield put(LoadInprogressStatsSuccess(inprogressStats?.data, inProgressActivePage));
    } catch {
        yield put(LoadInprogressStatsError());
    }
}

function* fetchInprogressStatsDelayed(action: LoadInprogressStatsStartDelayedAction): any {
    try {
        const { payload, type } = action;
        const { inProgressActivePage = yield select(getInprogressActivePage) } = payload;

        yield handleURLQuery(payload, type, inProgressActivePage);

        let loadInProgressStatsLoop = true;
        const loopId = yield select(getInProgressDelayedLoopId);

        while (loadInProgressStatsLoop) {
            const currentLoopId = yield select(getInProgressDelayedLoopId);
            if (loopId !== currentLoopId) {
                return;
            }

            const { location } = yield select(getRouterStore);
            if (
                location.pathname !== StatisticsPaths.StatisticsOverview &&
                location.pathname !== StatisticsPaths.StatisticsInprogress &&
                location.pathname !== StatisticsPaths.StatisticsLog
            ) {
                loadInProgressStatsLoop = false;
                return;
            }

            const countState = yield select(getInprogressDelayedCount);

            const inprogressStats = yield call(loadInProgressStats);

            //FIXME: Add proper data splicing here with lazyloading in table [PP]
            if (!isEmpty(inprogressStats.data.Domains)) {
                inprogressStats.data.Domains = slice(inprogressStats.data.Domains, 0, 100);
            }

            if ((countState || 0) !== inprogressStats?.data.Count) {
                yield put(SetLoaderInprogressStats(true));
                yield delay(2000);
                yield put(LoadInprogressStatsSuccessDelayed(inprogressStats?.data, inProgressActivePage));
                yield put(SetLoaderInprogressStats(false));
            } else {
                yield put(LoadInprogressStatsSuccessDelayed(inprogressStats?.data, inProgressActivePage));
            }

            yield delay(18000);
        }
    } catch {
        yield put(LoadInprogressStatsErrorDelayed());
    }
}

function* fetchCancelInprogress(): any {
    try {
        yield call(emailsCancel);
        yield put(LoadCancelInprogressSuccess());
        yield put(
            ShowToast({
                message: i18n.t('allinprogressemailscanceled'),
                severity: 'success',
                duration: 1500,
            })
        );
        yield put(LoadInprogressStatsStart(1));
    } catch (e) {
        yield put(LoadCancelInprogressError());
        yield call(errorToast, e);
    }
}

function* exportLog(action: ExportLogsAction): any {
    const { payload } = action;
    let checkExportStatus = true;
    yield put(ExportLogsStart());
    const eventsExportsApiCalls = payload.isNotification ? exportNotificationsEvents : exportEventslogs;
    try {
        const resp = yield call(eventsExportsApiCalls, payload);
        yield put(Push(ExportsPaths.Exports));

        while (checkExportStatus) {
            yield delay(5000);
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;
            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(status);
            }
        }
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('logsexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(LoadExports({ supressLoadIndicator: true }));

        yield put(ExportLogsSuccess());
    } catch (e) {
        yield put(ExportLogsError());
        yield call(errorToast, e);
    }
}
function* resend(action: ResendStartAction): any {
    const { payload } = action;

    try {
        yield call(resendByMessageid, { ...payload.params });
        yield put(
            ShowToast({
                severity: 'success',
                message: i18n.t('youremailsubmittedsuccessfully'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(ResendSuccess());
    } catch (e) {
        yield put(ResendError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchResend(): any {
    yield takeLatest('@@logs/RESEND_START', resend);
}

function* watchFetchLogs(): any {
    yield takeLatest('@@logs/LOAD_LOG_START', fetchLogs);
}
function* fetchNotificationLogsWatcher(): any {
    yield takeEvery(NotificationLogsTypes.LOAD, fetchNotificationLogs);
}

function* watchExportLog(): any {
    yield takeLatest('@@logs/EXPORT_LOG', exportLog);
}
function* watchFetchInprogressStats(): any {
    yield takeLatest('@@logs/LOAD_IN_PROGRESS_STATS_START', fetchInprogressStats);
}
function* watchFetchInprogressStatsDelayed(): any {
    yield takeEvery(LOAD_IN_PROGRESS_STATS_DELAYED_START, fetchInprogressStatsDelayed);
}
function* watchFetchCancelInprogress(): any {
    yield takeLatest('@@logs/CANCEL_IN_PROGRESS_START', fetchCancelInprogress);
}

function* watchFetchEventsCounts(): any {
    yield takeEvery(LogsActionTypesEnum.FETCH_EVENTS_COUNTS, fetchEventsCount);
}

const LogsSagas = [
    fork(watchFetchLogs),
    fork(watchExportLog),
    fork(watchFetchInprogressStats),
    fork(watchFetchInprogressStatsDelayed),
    fork(watchFetchCancelInprogress),
    fork(fetchNotificationLogsWatcher),
    fork(watchResend),
    fork(watchFetchEventsCounts),
];

export default LogsSagas;
